import React, { Component } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import SectionLabel from 'components/controls/sectionLabel';
import { GoX } from 'react-icons/go';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-monokai';
import Select from 'components/controls/select';
import StatusPill from '../../components/statusPill';
import { STATUSES } from '../../domain/schema';
import { upsertMission } from '../../domain/model';

/**
 * MissionPanel — detail panel shown in the right dock when no cell or dependency is selected. It is
 * the resting state of the detail panel; its × collapses the whole detail dock.
 *
 * The mission's Title and Description are editable here (missions have no draft gate, so they are
 * ALWAYS editable). Each field saves on blur via client.setMissionProps — which merges the change
 * into the mission graph object's OWN metadata, non-destructively to its cells — then reflects it
 * locally via onApplyLocal(upsertMission) so the switcher/panel update in place with no reload. The
 * write is skipped when the value is unchanged. Layout mirrors the Playground panel: a "Mission" section
 * (`Card.Text as="h6"` + `<hr/>`) holding two property sub-labels — Properties (the mission's own status
 * when set, plus the read-only Cells / Dependencies counts) and Status breakdown (the per-cell-status
 * counts) — then a "Details" section with the editable Title + Description. All values read from the store.
 *
 * It mirrors CellPanel's structure and classes (Card.Text as="h6" section headers, <hr/> separators,
 * the platform's gray-chip `.properties` / `.prop-name-column` table) so it renders identically with
 * no panel-specific CSS.
 *
 * The Missions widget has no top bar, so the mission switcher lives here: a "Mission" selector
 * section at the very top of the panel, above Properties / Status breakdown / Details. The node
 * feeds it the mission list + active id + an onSelect callback (via CellsSurface).
 *
 * Props:
 *   mission          — mission object { id, title?, description? } or null
 *   missionId        — string (the active mission id)
 *   cells            — array of cell vertex objects for the mission
 *   dependsOnEdges   — array of depends_on edges for the mission
 *   client           — SystemOfWorkClient instance
 *   onApplyLocal     — (updater: store => store) => void — incremental local store update
 *   onClose          — () => void  — collapse the detail dock
 *   missions         — array of all missions, for the switcher dropdown
 *   selectedMissionId — string — the active mission id (drives the switcher value)
 *   onSelectMission  — (missionId: string) => void — switch the active mission
 */
class MissionPanel extends Component {
  constructor(props) {
    super(props);
    const mission = props.mission || {};
    this._titleRef = React.createRef();
    this.state = {
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
    };
  }

  componentDidMount() {
    this._autoGrowTitle();
  }

  componentDidUpdate(prevProps) {
    // Re-seed the editable drafts only when the selected mission's identity changes — never on a plain
    // store update (which would clobber an in-progress edit).
    if ((prevProps.mission && prevProps.mission.id) !== (this.props.mission && this.props.mission.id)) {
      this._seedDrafts();
    }
    // Keep the title field sized to its content (covers seeding on mission switch).
    this._autoGrowTitle();
  }

  // Seed the editable title/description drafts from the current mission.
  _seedDrafts() {
    const mission = this.props.mission || {};
    this.setState({
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
    });
  }

  // Merge a metadata change into the store locally (no reload) so the switcher + panel reflect it.
  _applyLocal(changed) {
    const { mission, onApplyLocal } = this.props;
    if (!onApplyLocal || !mission) return;
    onApplyLocal(s => upsertMission(s, { ...mission, ...changed }));
  }

  async _saveTitle() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.titleDraft;
    const current = mission.title || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { title: next });
      this._applyLocal({ title: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveDescription() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.descriptionDraft;
    const current = mission.description || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { description: next });
      this._applyLocal({ description: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Grow the title textarea to fit its content (no inner scrollbar). Accepts the
  // live event target during typing, otherwise falls back to the mounted ref.
  _autoGrowTitle(el) {
    const node = el || this._titleRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }

  render() {
    const {
      mission, cells, dependsOnEdges, onClose,
      missions, selectedMissionId, onSelectMission,
    } = this.props;
    const { titleDraft, descriptionDraft } = this.state;

    // Mission switcher options (no "All missions" — a mission is always active).
    const missionOptions = (missions || []).map(m => ({ value: m.id, label: m.title || m.id }));
    const missionSelectValue =
      missionOptions.find(o => o.value === selectedMissionId) || missionOptions[0] || null;

    const cellList = cells || [];
    const cellCount = cellList.length;
    const depCount = (dependsOnEdges || []).length;
    // The mission may carry its own status; show it in Properties only when present.
    const missionStatus = (mission && mission.status) || null;

    // Status breakdown — counts per status, rendered in lifecycle order, omitting empty buckets.
    const counts = {};
    cellList.forEach(c => {
      const status = (c.props && c.props.status) || 'draft';
      counts[status] = (counts[status] || 0) + 1;
    });
    const presentStatuses = STATUSES.filter(s => counts[s]);

    return (
      <>
        {onClose && (
          <span className="workflow-controls-pane-right">
            <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
          </span>
        )}

        {/* Missions section — the active-mission switcher (was the former top-bar picker). */}
        <Card.Text as="h6">Missions</Card.Text>
        <hr />
        {onSelectMission && missionOptions.length > 0 && (
          <Form.Group className="mc-mission-panel-select">
            <Select
              options={missionOptions}
              value={missionSelectValue}
              onChange={opt => onSelectMission(opt ? opt.value : '')}
              menuPortalTarget={document.body}            />
            <Form.Text muted>
              Switch the active mission.
            </Form.Text>
          </Form.Group>
        )}

        {/* Mission section — two property subsections (Properties + Status breakdown), mirroring the
            Playground panel's layout: an h6 header + plain Card.Text sub-labels with gray-chip tables. */}
        <Card.Text as="h6">Mission</Card.Text>
        <hr />

        <SectionLabel>Properties</SectionLabel>
        <Card.Text>
          <table className="properties">
            <tbody>
              {missionStatus && (
                <tr>
                  <td className="prop-name-column">Status</td>
                  <td><StatusPill status={missionStatus} /></td>
                </tr>
              )}
              <tr>
                <td className="prop-name-column">Cells</td>
                <td className="prop-value-column">{cellCount}</td>
              </tr>
              <tr>
                <td className="prop-name-column">Dependencies</td>
                <td className="prop-value-column">{depCount}</td>
              </tr>
            </tbody>
          </table>
        </Card.Text>

        <SectionLabel>Status breakdown</SectionLabel>
        {presentStatuses.length === 0 ? (
          <Card.Text className="text-muted">No cells yet.</Card.Text>
        ) : (
          <Card.Text>
            <table className="properties">
              <tbody>
                {presentStatuses.map(s => (
                  <tr key={s}>
                    <td className="prop-name-column"><StatusPill status={s} /></td>
                    <td className="prop-value-column">{counts[s]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Text>
        )}

        {/* Details — editable Title + Description (missions have no draft gate, so always editable;
            saved on blur via client.setMissionProps, then reflected locally via onApplyLocal(upsertMission)). */}
        <Card.Text as="h6">Details</Card.Text>
        <hr />
        <Form.Group>
          <Form.Control
            ref={this._titleRef}
            as="textarea"
            rows={1}
            size="sm"
            value={titleDraft}
            placeholder="Mission title…"
            disabled={!mission}
            style={{ resize: 'none', overflow: 'hidden' }}
            onChange={e => { this.setState({ titleDraft: e.target.value }); this._autoGrowTitle(e.target); }}
            onBlur={() => this._saveTitle()}
          />
          <Form.Text muted>
            A short, descriptive name for this mission.
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <AceEditor
            mode="markdown"
            theme="monokai"
            width="100%"
            wrapEnabled
            minLines={4}
            maxLines={12}
            value={descriptionDraft}
            onChange={value => this.setState({ descriptionDraft: value })}
            onBlur={() => this._saveDescription()}
          />
          <Form.Text muted>
            Optional notes or context for this mission.
          </Form.Text>
        </Form.Group>
      </>
    );
  }
}

export default MissionPanel;
